// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, AppIcon, Badge, Button, IconButton, Modal, TextInput } from '@loa/ui-components';
import { fundsApi, type StrategyDTO } from '../../api/fundsApi';
import type { RecommendedFundLine } from '../../types/proposal';
import { FundSearchSelect } from './FundSearchSelect';
import './FundAllocationEditor.css';

export type FundAllocationEditorProps = Readonly<{
  value: RecommendedFundLine[];
  onChange: (lines: RecommendedFundLine[]) => void;
  disabled?: boolean;
  /** Allocation % is summed to 100 ± this tolerance for the advisory badge. */
  allocationTolerance?: number;
  testId?: string;
}>;

/** Sum of the set allocation %s (undefined when none set). */
function allocationSum(lines: RecommendedFundLine[]): number | undefined {
  const set = lines.filter((l) => l.allocationPercent != null);
  if (set.length === 0) return undefined;
  return set.reduce((acc, l) => acc + (l.allocationPercent ?? 0), 0);
}

const STATUS_COLOR: Record<string, 'blue' | 'green' | 'grey'> = {
  DRAFT: 'blue',
  PUBLISHED: 'green',
  ARCHIVED: 'grey',
};

/**
 * Multi-fund allocation editor built on {@link FundSearchSelect}. Manages a
 * list of {@link RecommendedFundLine}s (fund + optional allocation %), with a
 * per-line remove, an advisory sum-to-100 badge, a **Use portfolio** picker
 * (replaces the old "Load from strategy" dropdown — lists saved portfolios
 * with status/scope and an allocation preview, then applies the components as
 * lines) and a **Save as portfolio** affordance that turns the current
 * catalogue-backed lines into a DRAFT portfolio. Fully controlled — every
 * mutation goes through {@link onChange}.
 *
 * Used by the quote-backed PENSION/INVESTMENT recommendation section and the
 * flat Investment compose modal. Emits `fundKey` when the fund came from the
 * catalogue and leaves it undefined for a manually-typed custom fund.
 */
export function FundAllocationEditor({
  value,
  onChange,
  disabled = false,
  allocationTolerance = 0.5,
  testId = 'fund-allocation-editor',
}: FundAllocationEditorProps) {
  const [strategies, setStrategies] = useState<StrategyDTO[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [preview, setPreview] = useState<StrategyDTO | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);

  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fundsApi
      .listStrategies(0, 100)
      .then((res) => {
        if (cancelled) return;
        const visible = res.content.filter((s) => s.status !== 'ARCHIVED');
        // Published first — those are the vetted picks.
        visible.sort((a, b) => (a.status === b.status ? 0 : a.status === 'PUBLISHED' ? -1 : 1));
        setStrategies(visible);
      })
      .catch(() => {
        if (!cancelled) setStrategies([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const addLine = useCallback(
    (line: RecommendedFundLine) => {
      // De-dupe by catalogue key; manual entries are always appended.
      if (line.fundKey && value.some((l) => l.fundKey === line.fundKey)) return;
      onChange([...value, line]);
    },
    [value, onChange],
  );

  const removeLine = useCallback(
    (index: number) => onChange(value.filter((_, i) => i !== index)),
    [value, onChange],
  );

  const setAllocation = useCallback(
    (index: number, raw: string) => {
      const parsed = raw.trim() === '' ? undefined : Number(raw);
      const allocationPercent = parsed == null || Number.isNaN(parsed) ? undefined : parsed;
      onChange(value.map((l, i) => (i === index ? { ...l, allocationPercent } : l)));
    },
    [value, onChange],
  );

  const openPicker = () => {
    setPreviewId(null);
    setPreview(null);
    setPreviewError(null);
    setPickerOpen(true);
  };

  const selectForPreview = useCallback(async (id: string) => {
    setPreviewId(id);
    setPreview(null);
    setPreviewError(null);
    try {
      setPreview(await fundsApi.getStrategy(id));
    } catch {
      setPreviewError('Could not load this portfolio.');
    }
  }, []);

  /** Apply the previewed portfolio: hydrate fund names and REPLACE the lines. */
  const applyPreview = useCallback(async () => {
    if (!preview) return;
    setSeeding(true);
    try {
      const lines = await Promise.all(
        preview.components.map(async (c) => {
          try {
            const fund = await fundsApi.get(c.fundKey);
            return {
              fundKey: c.fundKey,
              fundName: fund.fundName,
              providerName: fund.providerDisplayName ?? undefined,
              allocationPercent: c.allocationPct ?? undefined,
            } satisfies RecommendedFundLine;
          } catch {
            return {
              fundKey: c.fundKey,
              fundName: c.fundKey,
              allocationPercent: c.allocationPct ?? undefined,
            } satisfies RecommendedFundLine;
          }
        }),
      );
      onChange(lines);
      setPickerOpen(false);
    } finally {
      setSeeding(false);
    }
  }, [preview, onChange]);

  const catalogueLines = useMemo(() => value.filter((l) => l.fundKey), [value]);
  const manualLines = useMemo(() => value.filter((l) => !l.fundKey), [value]);
  const catalogueLinesAllocated =
    catalogueLines.length > 0 && catalogueLines.every((l) => (l.allocationPercent ?? 0) > 0);

  const saveAsPortfolio = async () => {
    try {
      setSaving(true);
      setSaveError(null);
      const created = await fundsApi.createStrategy({
        name: saveName.trim(),
        description: null,
        riskProfile: null,
        components: catalogueLines.map((l, i) => ({
          fundKey: l.fundKey!,
          allocationPct: l.allocationPercent ?? 0,
          sortOrder: i,
        })),
      });
      setStrategies((prev) => [created, ...prev]);
      setSaveOpen(false);
      setSaveName('');
      setSavedNotice(`Saved "${created.name}" as a draft portfolio.`);
    } catch (e: unknown) {
      const data = (e as { response?: { data?: { message?: string } } })?.response?.data;
      setSaveError(data?.message ?? (e instanceof Error ? e.message : 'Failed to save the portfolio'));
    } finally {
      setSaving(false);
    }
  };

  const sum = allocationSum(value);
  const sumOk = sum == null || Math.abs(sum - 100) <= allocationTolerance;

  return (
    <div className="fund-allocation-editor" data-testid={testId}>
      {savedNotice && (
        <Alert variant="success" dismissible onDismiss={() => setSavedNotice(null)}>
          {savedNotice}
        </Alert>
      )}

      {value.length === 0 && !disabled && strategies.length > 0 && (
        <div className="fund-allocation-editor__empty-cta">
          <Button
            variant="outlined"
            size="sm"
            onClick={openPicker}
            data-testid={`${testId}-empty-cta`}
          >
            Start from a portfolio
          </Button>
        </div>
      )}

      {value.length > 0 && (
        <ul className="fund-allocation-editor__lines" data-testid={`${testId}-lines`}>
          {value.map((line, i) => (
            <li
              key={`${line.fundKey ?? 'manual'}-${i}`}
              className="fund-allocation-editor__line"
              data-testid={`${testId}-line-${i}`}
            >
              <div className="fund-allocation-editor__line-name">
                <span className="fund-allocation-editor__fund-name">{line.fundName}</span>
                {line.providerName && (
                  <span className="fund-allocation-editor__fund-provider">
                    {line.providerName}
                  </span>
                )}
              </div>
              <div className="fund-allocation-editor__line-alloc">
                <TextInput
                  type="number"
                  value={line.allocationPercent != null ? String(line.allocationPercent) : ''}
                  onChange={(e) => setAllocation(i, e.target.value)}
                  placeholder="%"
                  disabled={disabled}
                  aria-label={`Allocation percent for ${line.fundName}`}
                  data-testid={`${testId}-alloc-${i}`}
                />
              </div>
              {!disabled && (
                <IconButton
                  variant="ghost"
                  size="sm"
                  icon={<AppIcon name="trash" />}
                  onClick={() => removeLine(i)}
                  aria-label={`Remove ${line.fundName}`}
                  data-testid={`${testId}-remove-${i}`}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {value.length > 0 && sum != null && (
        <div
          className={`fund-allocation-editor__sum${sumOk ? '' : ' fund-allocation-editor__sum--warn'}`}
          data-testid={`${testId}-sum`}
        >
          Total allocation: {sum}%{sumOk ? '' : ' (should total 100%)'}
        </div>
      )}

      {!disabled && (
        <div className="fund-allocation-editor__add">
          <FundSearchSelect
            onSelect={(fund) =>
              addLine({
                fundKey: fund.fundKey,
                fundName: fund.fundName,
                providerName: fund.providerDisplayName ?? undefined,
              })
            }
            onManualSelect={(fundName) => addLine({ fundName })}
            testId={`${testId}-search`}
          />
          <div className="fund-allocation-editor__strategy">
            <Button
              variant="outlined"
              size="sm"
              onClick={openPicker}
              disabled={strategies.length === 0}
              data-testid={`${testId}-strategy`}
            >
              Use portfolio
            </Button>
            <Button
              variant="link"
              size="sm"
              onClick={() => {
                setSaveError(null);
                setSaveOpen(true);
              }}
              disabled={!catalogueLinesAllocated || saving}
              title={
                catalogueLinesAllocated
                  ? undefined
                  : 'Every catalogue fund needs an allocation % before saving'
              }
              data-testid={`${testId}-save-as`}
            >
              Save as portfolio
            </Button>
          </div>
        </div>
      )}

      {pickerOpen && (
        <Modal open onClose={() => setPickerOpen(false)} size="medium">
          <Modal.Header title="Use portfolio" onClose={() => setPickerOpen(false)} />
          <Modal.Content>
            <div className="fund-allocation-editor__picker" data-testid={`${testId}-picker`}>
              <ul className="fund-allocation-editor__picker-list">
                {strategies.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      className={`fund-allocation-editor__picker-item${
                        previewId === s.id ? ' fund-allocation-editor__picker-item--active' : ''
                      }`}
                      onClick={() => void selectForPreview(s.id)}
                    >
                      <span className="fund-allocation-editor__picker-name">{s.name}</span>
                      <Badge color={STATUS_COLOR[s.status] ?? 'grey'} size="sm">
                        {s.status}
                      </Badge>
                      {s.scope === 'BROKERAGE' && (
                        <Badge color="purple" size="sm">
                          Brokerage
                        </Badge>
                      )}
                    </button>
                  </li>
                ))}
              </ul>

              {previewError && <Alert variant="error">{previewError}</Alert>}

              {preview && (
                <div className="fund-allocation-editor__picker-preview" data-testid={`${testId}-preview`}>
                  <ul>
                    {preview.components.map((c) => (
                      <li key={c.fundKey}>
                        <span className="fund-allocation-editor__fund-name">{c.fundKey}</span>
                        <span>{c.allocationPct != null ? `${c.allocationPct}%` : '—'}</span>
                      </li>
                    ))}
                  </ul>
                  {value.length > 0 && (
                    <p className="fund-allocation-editor__picker-note">
                      Applying replaces the current fund lines.
                    </p>
                  )}
                </div>
              )}

              <div className="fund-allocation-editor__picker-actions">
                <Button variant="outlined" size="sm" onClick={() => setPickerOpen(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => void applyPreview()}
                  loading={seeding}
                  disabled={!preview || seeding}
                  data-testid={`${testId}-apply-portfolio`}
                >
                  Apply portfolio
                </Button>
              </div>
            </div>
          </Modal.Content>
        </Modal>
      )}

      {saveOpen && (
        <Modal open onClose={() => setSaveOpen(false)} size="small">
          <Modal.Header title="Save as portfolio" onClose={() => setSaveOpen(false)} />
          <Modal.Content>
            <div className="fund-allocation-editor__save-dialog">
              <TextInput
                label="Portfolio name"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="e.g. Balanced Growth 2026"
                required
              />
              {manualLines.length > 0 && (
                <p className="fund-allocation-editor__picker-note">
                  {manualLines.length} manually-typed fund
                  {manualLines.length > 1 ? 's are' : ' is'} not in the catalogue and will be
                  left out of the portfolio.
                </p>
              )}
              {saveError && <Alert variant="error">{saveError}</Alert>}
              <div className="fund-allocation-editor__picker-actions">
                <Button variant="outlined" size="sm" onClick={() => setSaveOpen(false)} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => void saveAsPortfolio()}
                  loading={saving}
                  disabled={saving || saveName.trim() === ''}
                  data-testid={`${testId}-confirm-save-as`}
                >
                  Save draft
                </Button>
              </div>
            </div>
          </Modal.Content>
        </Modal>
      )}
    </div>
  );
}

export default FundAllocationEditor;
